/**
 * ResolutionPlaybook Component
 *
 * Displays how a similar issue was resolved
 * Shows resolver, time to resolve, and resolution notes
 */

import React from 'react';
import {
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const ResolutionPlaybook = ({ resolution, issueId }) => {
  const formatTimeToResolve = (hours) => {
    if (!hours) return 'Unknown';

    if (hours < 1) {
      const minutes = Math.round(hours * 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }

    if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }

    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);

    if (remainingHours === 0) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    }

    return `${days}d ${remainingHours}h`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';

    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Unknown';
    }
  };

  return (
    <div className="p-4 bg-white border-t border-gray-200">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircleIcon className="h-5 w-5 text-green-600" />
        <h4 className="text-sm font-semibold text-gray-900">How It Was Resolved</h4>
      </div>

      <div className="space-y-3">
        {/* Resolver Info */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {resolution.resolved_by?.avatar_url ? (
              <img
                src={resolution.resolved_by.avatar_url}
                alt={resolution.resolved_by_name}
                className="h-10 w-10 rounded-full border-2 border-gray-200"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center border-2 border-gray-200">
                <UserIcon className="h-5 w-5 text-blue-600" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-900">
                {resolution.resolved_by_name || 'Unknown'}
              </span>
              {resolution.resolved_by?.email && (
                <span className="text-xs text-gray-500">
                  {resolution.resolved_by.email}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
              {/* Time to Resolve */}
              <div className="flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                <span>Resolved in {formatTimeToResolve(resolution.time_to_resolve_hours)}</span>
              </div>

              {/* Resolution Date */}
              {resolution.resolved_at && (
                <span className="text-gray-400">
                  on {formatDate(resolution.resolved_at)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Resolution Notes */}
        {resolution.resolution_notes && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-2">
              <DocumentTextIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h5 className="text-xs font-semibold text-green-900 mb-1">Resolution Notes:</h5>
                <p className="text-sm text-green-800 whitespace-pre-wrap">
                  {resolution.resolution_notes}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Resolution Steps (if available) */}
        {resolution.resolution_steps && resolution.resolution_steps.length > 0 && (
          <div className="mt-3">
            <h5 className="text-xs font-semibold text-gray-700 mb-2">Resolution Steps:</h5>
            <ol className="space-y-1 text-sm text-gray-600">
              {resolution.resolution_steps.map((step, idx) => (
                <li key={idx} className="flex gap-2">
                  <span className="font-medium text-gray-900">{idx + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* View Full Issue Link */}
        <div className="pt-2 border-t border-gray-200">
          <a
            href={`/issues/${issueId}`}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = `/issues/${issueId}`;
            }}
          >
            View full issue details →
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResolutionPlaybook;
